import React, { useEffect, useState } from "react"
import { Modal, Button, Tab, Tabs } from "react-bootstrap"

import ReactSelectionElement from "../widgets/ReactSelectionElement"
import { withFavouritesConfig } from "../config/FavouriteConfigProvider"

const FavouriteEditor = ({
  show,
  onHide,
  encoded,
  title,
  initialName,
  initialDescription = "",
  graphInterfaces = [],
  replaceChoices,
  nameCheck,
  onCreate,
  onUpdate,
  onDelete,
  onRestore,
  getText,
  is_config_editor,
  editable_configs = [],
}) => {
  const registration = {
    name_label: getText("general.name", "Name"),
    desc_label: getText("general.description", "Description"),
    replace_label: getText("general.replace", "Replace"),
  }
  const proposeName = (baseName, nameCheck) => {
    const finalBaseName = baseName || getText("general.unknown", "Unknown")

    if (nameCheck && nameCheck(finalBaseName)) {
      for (let n = 2; ; ++n) {
        const prop = `${finalBaseName} ${n}`
        if (!nameCheck(prop)) return prop
      }
    } else {
      return finalBaseName
    }
  }
  initialName = proposeName(initialName, nameCheck)

  const [name, setName] = useState(initialName)
  const [replace, setReplace] = useState("")
  const [description, setDescription] = useState(initialDescription)
  const [disabled, setDisabled] = useState(false)
  const [selectedTab, setSelectedTab] = useState("Object0")
  const [saveAsDefaultConfig, setSaveAsDefaultConfig] = useState(false)
  const [interfacesToUpdate, setInterfacesToUpdate] = useState([])

  useEffect(() => {
    if (!show) {
      return
    }

    setReplace("")
    setName(initialName)
    setDescription(initialDescription)
    setDisabled(false)
    if (graphInterfaces.length) {
      setSaveAsDefaultConfig(true)
      setInterfacesToUpdate(graphInterfaces)
    }
  }, [show])

  useEffect(() => {
    if (!saveAsDefaultConfig && interfacesToUpdate.length) {
      // If Favourite is not being saved as default config, then wipe interfaces
      setInterfacesToUpdate([])
    }
  }, [saveAsDefaultConfig])

  const selectTab = (tab) => setSelectedTab(tab)

  const validateName = () => {
    let nameError = null
    if (!name || name.length < 3) {
      nameError = getText("favourites.validation.name.length", "Favourite name is too short")
    } else {
      // == and != (instead of === and !==) because str<->int
      if (nameCheck && name != replaceChoices?.find(({ id }) => id == replace)?.name && nameCheck(name)) {
        nameError = getText("favourites.validation.name.uniqueness", "Favourite name must be unique")
      }
    }
    return nameError
  }

  const onFormSubmit = (ev) => {
    ev.preventDefault()
    ev.stopPropagation()

    if (validateName()) return

    if (!disabled) {
      if (onUpdate) {
        onUpdate(name, description, interfacesToUpdate)
      } else if (onCreate) {
        onCreate(name, description, replace, interfacesToUpdate)
      }
    }

    setDisabled(true)
  }

  const nameError = validateName()
  return (
    <Modal size={"lg"} dialogClassName={"definitions-modal"} show={show} onHide={onHide} backdrop={"static"}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className={"form-horizontal"} style={{ padding: "15px" }} onSubmit={onFormSubmit}>
          {onCreate && (
            <div>
              <label htmlFor={"replace"} className={"col-form-label"}>
                {registration.replace_label}:
              </label>
              &nbsp;
              <select
                id={"replace"}
                className={"form-select"}
                value={replace}
                onChange={(ev) => {
                  const replaceId = ev.target.value
                  setReplace(replaceId)
                  const replaceChoice = replaceChoices.find(({ id }) => id == replaceId) // == because str<->int
                  setName(replaceChoice ? replaceChoice.name : initialName)
                  setDescription(replaceChoice ? replaceChoice.description : initialDescription)
                  setSaveAsDefaultConfig(!!(replaceChoice && replaceChoice.graph_interfaces.length))
                  setInterfacesToUpdate(replaceChoice ? replaceChoice.graph_interfaces : [])
                }}
              >
                <option key="" value="">
                  ({getText("general.create.new", "Create New")})
                </option>
                {replaceChoices.map((replaceChoice) => (
                  <option key={replaceChoice.id} value={replaceChoice.id}>
                    {`${replaceChoice.name}${replaceChoice.view ? ` (${replaceChoice.view})` : ""}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className={`${nameError ? "has-error" : ""}`}>
            <label htmlFor={"favname"} className={"col-form-label text-center"}>
              {registration.name_label}*:
            </label>
            &nbsp;
            <div>
              <input
                type={"text"}
                id={"favname"}
                className={"form-control"}
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                placeholder={registration.name_label}
                autoFocus={true}
                autoComplete={"off"}
              />
              <span className={"form-text error-message"}>{nameError}</span>
            </div>
          </div>
          <div>
            <label htmlFor={"favdesc"} className={"col-form-label"}>
              {registration.desc_label}:
            </label>
            &nbsp;
            <div className={"one-rem-margin-bottom"}>
              <input
                type={"text"}
                id={"favdesc"}
                className={"form-control"}
                value={description}
                onChange={(ev) => setDescription(ev.target.value)}
                placeholder={`${registration.desc_label} (${getText("general.optional", "Optional")})`}
                autoComplete={"off"}
              />
            </div>
          </div>
          {is_config_editor && encoded && encoded.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}>
                <input
                  type="checkbox"
                  name="saveAsDefaultConfig"
                  checked={saveAsDefaultConfig}
                  onChange={() => setSaveAsDefaultConfig(!saveAsDefaultConfig)}
                />
                &nbsp; {getText("favourites.save.as.default.config", "Save as Default Graph Config")}
              </div>
              <div style={{ flex: 1 }}>
                {saveAsDefaultConfig && (
                  <ReactSelectionElement
                    list={editable_configs}
                    name={"editableConfigs"}
                    label={getText("favourites.assign.to.interface", "Assign to Interface(s)")}
                    getOptionLabel={(obj) => obj.name}
                    getOptionValue={(obj) => obj.id}
                    defaultValue={interfacesToUpdate}
                    onValueChange={(_, e) => setInterfacesToUpdate(e)}
                  />
                )}
              </div>
            </div>
          )}
          <div style={{ marginTop: "25px" }}>
            {encoded && encoded.length > 0 && (
              <Tabs id={"favourites-editor-tabs"} activeKey={selectedTab} onSelect={selectTab}>
                {(encoded || [])
                  .filter((item) => item)
                  .map((item, idx) => (
                    <Tab
                      eventKey={`Object${idx}`}
                      style={{
                        padding: "15px",
                        paddingBottom: "0px",
                        marginTop: "20px",
                      }}
                      key={`Object${idx}`}
                      title={item?.title}
                    >
                      {[
                        {
                          key: "title",
                          text: getText("general.title", "Title"),
                        },
                        {
                          key: "axis1label",
                          text: getText("graphing.y1.axis.label", "Y1 Axis Label"),
                        },
                        {
                          key: "axis2label",
                          text: getText("graphing.y2.axis.label", "Y2 Axis Label"),
                        },
                      ].map((metric) => {
                        if (item[metric.key]) {
                          return (
                            <div key={`${metric.key}${idx}`}>
                              <u>
                                <label style={{ marginRight: "5px" }}>{metric.text}:</label>
                              </u>
                              &nbsp;
                              <span>{item[metric.key]}</span>
                            </div>
                          )
                        }
                      })}
                    </Tab>
                  ))}
              </Tabs>
            )}
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onHide} variant={"outline-primary"}>
          {getText("general.closeButton", "Close")}
        </Button>
        {onDelete && (
          <Button
            variant={"danger"}
            onClick={() => {
              !disabled && onDelete ? onDelete() : null
              setDisabled(true)
            }}
            disabled={disabled}
          >
            {getText("general.delete", "Delete")}
          </Button>
        )}
        {onRestore && (
          <Button
            variant={"warning"}
            onClick={() => {
              !disabled && onRestore ? onRestore(interfacesToUpdate) : null
              setDisabled(true)
            }}
            disabled={disabled}
          >
            {getText("general.restore", "Restore")}
          </Button>
        )}
        <Button
          variant={"primary"}
          onClick={onFormSubmit}
          disabled={onRestore ? true : disabled || !!nameError}
        >
          {onUpdate
            ? getText("general.update", "Update")
            : replace
              ? registration.replace_label
              : getText("general.create", "Create")}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default withFavouritesConfig(FavouriteEditor)
